import styled from 'styled-components';
import { colors, radius } from '../../../../styles/tokens';

export const Container = styled.div`
  padding: 40px;
  min-height: 100vh;

  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 1199px) {
    padding: 20px;
  }
`;

export const BackLink = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;

  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.primary};
`;

interface ExpirationBannerProps {
  severity: 'expired' | 'expiring';
  warning: boolean;
}

export const ExpirationBanner = styled.div<ExpirationBannerProps>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  padding: 16px 20px;
  border-radius: ${radius.md};

  background-color: ${({ severity, warning }) => {
    if (!warning) return colors.surfaceContainerLow;
    return severity === 'expired' ? `${colors.error}1a` : `${colors.primary}1a`;
  }};
  border: 1px solid
    ${({ severity, warning }) => {
      if (!warning) return colors.outlineVariant;
      return severity === 'expired' ? colors.error : colors.primary;
    }};

  color: ${colors.onSurface};
  font-size: 14px;
`;

export const ExpirationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const ExpirationOverrideInput = styled.input`
  width: 64px;
  padding: 6px 8px;
  border-radius: ${radius.DEFAULT};
  border: 1px solid ${colors.outlineVariant};
  background-color: ${colors.surfaceContainerHigh};
  color: ${colors.onSurface};
  font-size: 13px;
  text-align: center;

  /* Remove as setas do input numérico (Chrome/Safari/Edge e Firefox). */
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const ExpirationOverrideLabel = styled.label`
  font-size: 12.5px;
  color: ${colors.onSurfaceVariant};
`;

// Confirmação explícita do prazo -- mesmo padrão do app staff (não dá
// pra confiar só no blur do input em todo cenário).
export const SaveOverrideButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 36px;
  height: 36px;
  flex-shrink: 0;

  background-color: ${colors.primary};
  border: none;
  border-radius: ${radius.DEFAULT};
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SummarySection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;

  @media (max-width: 1199px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const SummaryLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const ContainerAvatar = styled.div`
  width: 56px;
  height: 56px;

  border-radius: 50%;
  border: 2px solid ${colors.primary};

  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    max-height: 100%;
    width: auto;
  }
`;

export const StudentName = styled.h1`
  font-size: 36px;
  font-weight: 800;
  color: ${colors.onSurface};
  letter-spacing: -0.02em;
`;

export const StudentDescription = styled.p`
  color: ${colors.onSurfaceVariant};
  font-size: 14px;
  max-width: 520px;

  strong {
    color: ${colors.primary};
    font-style: italic;
  }
`;

export const LastTraining = styled.p`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.onSurfaceVariant};
`;

export const ChipsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

export const Chip = styled.div`
  padding: 12px 16px;

  background-color: ${colors.surfaceContainerLow};
  border-radius: ${radius.DEFAULT};
  border-bottom: 2px solid ${colors.primary}30;

  p {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${colors.onSurfaceVariant};
    margin-bottom: 4px;
  }

  span {
    font-weight: 700;
    color: ${colors.onSurface};
  }
`;

export const EditorToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

export const SegmentedTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  padding: 4px;

  background-color: ${colors.surfaceContainerLow};
  border-radius: ${radius.DEFAULT};

  overflow-x: auto;
`;

export const AddDayButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 36px;
  height: 36px;
  flex-shrink: 0;

  background: transparent;
  border: none;
  border-radius: ${radius.sm};
  color: ${colors.onSurfaceVariant};
  cursor: pointer;

  &:hover {
    color: ${colors.primary};
  }
`;

export const SelectWrapper = styled.div`
  min-width: 220px;
`;

export const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

export const ToolbarActions = styled.div`
  display: flex;
  gap: 12px;
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  padding: 0 20px;
  height: 40px;

  background: transparent;
  border: 1px solid ${colors.outlineVariant};
  border-radius: ${radius.sm};
  cursor: pointer;

  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.onSurfaceVariant};

  &:hover {
    border-color: ${colors.primary}80;
    color: ${colors.onSurface};
  }
`;

export const GradientButton = styled.button`
  padding: 0 24px;
  height: 40px;

  background: linear-gradient(
    135deg,
    ${colors.primary} 0%,
    ${colors.primaryContainer} 100%
  );
  border: none;
  border-radius: ${radius.sm};
  cursor: pointer;

  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.onPrimary};

  box-shadow: 0 4px 20px ${colors.primary}30;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  align-items: start;

  @media (max-width: 1199px) {
    grid-template-columns: 1fr;
  }
`;

export const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
`;

export const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: -0.01em;
  color: ${colors.onSurface};

  display: flex;
  align-items: center;
  gap: 8px;

  span {
    color: ${colors.primaryContainer};
  }
`;

export const EstimatedTime = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${colors.onSurfaceVariant};
`;

export const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const AddExerciseButton = styled.button`
  width: 100%;
  padding: 28px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  background: transparent;
  border: 2px dashed ${colors.outlineVariant}80;
  border-radius: ${radius.md};
  cursor: pointer;

  color: ${colors.onSurfaceVariant};

  transition: color 0.2s, border-color 0.2s;

  span.label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }

  &:hover {
    color: ${colors.primary};
    border-color: ${colors.primary}60;
  }
`;

export const SectionSummary = styled.p`
  font-size: 13px;
  color: ${colors.onSurfaceVariant};

  strong {
    color: ${colors.onSurface};
  }
`;
